import { Truck, Car, Van, Bus, Motorcycle } from "@phosphor-icons/react";

export const SYMBOLS = [
  { key: "Truck", icon: Truck }, { key: "Trailer", icon: Truck }, { key: "Tractor", icon: Truck },
  { key: "Car", icon: Car }, { key: "Van", icon: Van }, { key: "Bus", icon: Bus }, { key: "Bike", icon: Motorcycle },
];
export const LICENSE_TYPES = ["Annual AIS-140", "Lifetime", "Quarterly", "Monthly"];
export const VALIDITY = ["1 Year", "2 Years", "3 Years", "Lifetime", "Custom"];
export const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2.5 text-sm focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none";
export const labelCls = "block text-[12px] font-semibold text-muted-foreground mb-1.5";

export const DEVICE_DEFAULTS = {
  device_model: "Generic AIS140", vehicle_type: "Truck", vehicle_group_id: "", sim_number: "", sim2: "",
  driver_number: "", initial_state: "inactive", license_expiry: "", license_type: "Annual AIS-140", validity: "1 Year",
  speed_limit: 80, opening_odometer: 0, idle_threshold: 5,
  ignition_connected: true, parking_mode: false, overspeed_alerts: true, disable_login: false, remarks: "",
  enable_data_push: false, gov_vltd_push: false, customer_webhook: false,
};

export function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-border rounded-md overflow-hidden">
      <div className="px-4 py-3 bg-[#f7f8fa] border-b border-border">
        <div className="font-bold text-sm">{title}</div>
        {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// Renders the shared Assignment / Advanced / Data-push sections used by Add + Edit device forms.
export function DeviceFormSections({ f, set, groups = [] }) {
  return (
    <>
      <Section title="Assignment, SIM & license" subtitle="Fleet mapping and subscription validity">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Vehicle group</label>
            <select data-testid="dev-group" value={f.vehicle_group_id} onChange={(e) => set("vehicle_group_id", e.target.value)} className={inputCls}>
              <option value="">Ungrouped</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>SIM number 1</label>
            <input data-testid="dev-sim1" value={f.sim_number} onChange={(e) => set("sim_number", e.target.value)} placeholder="Primary ICCID / mobile number" className={inputCls} /></div>
          <div><label className={labelCls}>SIM number 2</label>
            <input value={f.sim2} onChange={(e) => set("sim2", e.target.value)} placeholder="Backup ICCID / mobile number" className={inputCls} /></div>
          <div><label className={labelCls}>Device model</label>
            <input data-testid="dev-model" value={f.device_model} onChange={(e) => set("device_model", e.target.value)} placeholder="Device model" className={inputCls} /></div>
          <div><label className={labelCls}>Driver number</label>
            <input value={f.driver_number} onChange={(e) => set("driver_number", e.target.value)} placeholder="Driver mobile number" className={inputCls} /></div>
          <div><label className={labelCls}>Initial server state</label>
            <select data-testid="dev-state" value={f.initial_state} onChange={(e) => set("initial_state", e.target.value)} className={inputCls}>
              <option value="inactive">Inactive</option>
              <option value="active">Active</option>
            </select></div>
          <div><label className={labelCls}>License expiration</label>
            <input type="date" value={f.license_expiry} onChange={(e) => set("license_expiry", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>License type</label>
            <select value={f.license_type} onChange={(e) => set("license_type", e.target.value)} className={inputCls}>
              {LICENSE_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select></div>
          <div><label className={labelCls}>Validity</label>
            <select value={f.validity} onChange={(e) => set("validity", e.target.value)} className={inputCls}>
              {VALIDITY.map((t) => <option key={t}>{t}</option>)}
            </select></div>
        </div>
        <div className="mt-4">
          <div className="text-[11px] font-bold tracking-wide text-muted-foreground uppercase">Map symbol</div>
          <div className="text-[11px] text-muted-foreground mb-2">Choose the vehicle body used across tracking and playback.</div>
          <div className="flex flex-wrap gap-2.5">
            {SYMBOLS.map((s) => {
              const Icon = s.icon; const active = f.vehicle_type === s.key;
              return (
                <button key={s.key} type="button" data-testid={`dev-symbol-${s.key}`} onClick={() => set("vehicle_type", s.key)}
                  className={`relative flex flex-col items-center justify-center gap-1 w-24 h-20 rounded-md border-2 transition-colors ${active ? "border-primary bg-primary/5" : "border-border bg-white hover:border-primary/50"}`}>
                  {active && <span className="absolute top-1 right-1 text-[8px] font-bold bg-primary text-white px-1.5 py-0.5 rounded">Selected</span>}
                  <Icon size={26} weight="fill" className={active ? "text-primary" : "text-slate-500"} />
                  <span className="text-[11px] font-semibold">{s.key}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Section>

      <Section title="Advanced settings" subtitle="Limits, odometer, ignition and custom device attributes">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className={labelCls}>Speed limit (km/h)</label>
            <input data-testid="dev-speed" type="number" value={f.speed_limit} onChange={(e) => set("speed_limit", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Opening odometer (km)</label>
            <input type="number" value={f.opening_odometer} onChange={(e) => set("opening_odometer", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Idle threshold (minutes)</label>
            <input type="number" value={f.idle_threshold} onChange={(e) => set("idle_threshold", e.target.value)} className={inputCls} /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-4">
          {[["ignition_connected", "Ignition wire is connected"], ["parking_mode", "Parking mode"], ["overspeed_alerts", "Overspeed alerts"], ["disable_login", "Disable device login"]].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2.5 cursor-pointer hover:border-primary/50">
              <input data-testid={`dev-${k}`} type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="accent-primary" /> {lbl}
            </label>
          ))}
        </div>
        <div className="mt-4">
          <label className={labelCls}>Remarks</label>
          <input value={f.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Installation or customer notes" className={inputCls} />
        </div>
      </Section>

      <Section title="Data push & integrations" subtitle="Forward normalized telemetry to an approved endpoint">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
          {[["enable_data_push", "Enable data push"], ["gov_vltd_push", "Government VLTD push"], ["customer_webhook", "Customer webhook"]].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2.5 cursor-pointer hover:border-primary/50">
              <input data-testid={`dev-${k}`} type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="accent-primary" /> {lbl}
            </label>
          ))}
        </div>
      </Section>
    </>
  );
}
